import React from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from '@/context/AppContext';
import useChromeLayer from '@/hooks/useChromeLayer';
import { iconBoxStyle, iconGlyph } from '@/theme/icons';
import { moduleBox } from '@/theme/header-modules';
import {
    VIDEO_CAPTION,
    VIDEO_PLAY,
    VIDEO_SCRUBBER,
    VIDEO_TIME,
    ICON_PAUSE,
    ICON_PLAY,
    videoChromeBox,
    videoControlsLayout,
    videoTextStyle,
    videoTimeLabel,
    videoTimeStyle,
    videoTrackThickness,
} from '@/theme/video-chrome';
import { resolveFace, resolveGlyph } from '@/library';
import GlyphPreview from '@components/library/GlyphPreview';
import { previewLibrary, useHoverGlyph } from '@/preview/hoverChrome';
import { useMediaTitle } from './MediaFrame';

function ChromeHandle({ id, box, className, style, children }) {
    const placed = iconBoxStyle(box);
    return (
        <span
            data-header-module={id}
            data-lock-ratio="0"
            className={className}
            style={{ color: 'var(--color-text)', ...style, ...placed }}
        >
            {children}
        </span>
    );
}

ChromeHandle.propTypes = {
    id: PropTypes.string.isRequired,
    box: PropTypes.object,
    className: PropTypes.string,
    style: PropTypes.object,
    children: PropTypes.node,
};

/**
 * Play, scrubber, time, and caption for the public video player.
 * Each piece is a header module so Appearance can place and style it.
 */
export default function VideoControls({
    isPlaying,
    currentTime,
    duration,
    description,
    onToggle,
    onSeek,
}) {
    const { theme, content } = useAppContext();
    const layer = useChromeLayer();
    const modules = theme?.header?.modules;
    const library = previewLibrary(content);
    const playHover = useHoverGlyph(isPlaying ? ICON_PAUSE : ICON_PLAY);
    const mediaTitle = useMediaTitle({ videoFallback: true });

    const playBox = videoChromeBox(modules, VIDEO_PLAY, layer);
    const scrubberBox = videoChromeBox(modules, VIDEO_SCRUBBER, layer);
    const timeBox = videoChromeBox(modules, VIDEO_TIME, layer);
    const captionBox = videoChromeBox(modules, VIDEO_CAPTION, layer);

    const playStored = moduleBox(modules, VIDEO_PLAY, layer);
    const scrubberStored = moduleBox(modules, VIDEO_SCRUBBER, layer);
    const timeStored = moduleBox(modules, VIDEO_TIME, layer);

    const playGlyphId = playHover !== undefined
        ? (playHover || iconGlyph(modules, isPlaying ? ICON_PAUSE : ICON_PLAY, layer))
        : iconGlyph(modules, isPlaying ? ICON_PAUSE : ICON_PLAY, layer);
    const playGlyph = resolveGlyph(theme?.icons?.set, playGlyphId, library);
    const progress = duration > 0
        ? Math.max(0, Math.min(100, (currentTime / duration) * 100))
        : 0;
    const thickness = videoTrackThickness(scrubberBox);
    const layout = videoControlsLayout({ play: playStored, scrubber: scrubberStored, time: timeStored });
    const { playWidth, playHeight } = layout;
    const timeStyle = { ...videoTimeStyle(timeBox, library, resolveFace), fontVariantNumeric: 'tabular-nums' };
    const timeLabel = videoTimeLabel(currentTime, duration, timeBox.format);

    // Each control owns a fixed grid cell. A control dragged to a free spot is
    // fixed-positioned, so its cell keeps an invisible stand-in of the same size
    // and the controls still in the row never shift.
    return (
        <>
            <div className="mx-auto grid w-full max-w-5xl grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-2 sm:px-0">
                <div className="relative flex items-center justify-center" style={{ minWidth: playWidth, minHeight: playHeight }}>
                    <ChromeHandle
                        id={VIDEO_PLAY}
                        box={playStored}
                        className={layout.playClass}
                    >
                        <button
                            type="button"
                            onClick={onToggle}
                            className="inline-flex items-center justify-center transition-colors [&_svg]:h-full [&_svg]:w-full"
                            style={{
                                width: playWidth,
                                height: playHeight,
                                color: playBox.color || 'var(--color-text)',
                            }}
                            aria-label={isPlaying ? 'Pause video' : 'Play video'}
                            title={isPlaying ? 'Pause' : 'Play'}
                        >
                            <GlyphPreview glyph={playGlyph} />
                        </button>
                    </ChromeHandle>
                </div>

                <div className="relative flex h-5 min-w-0 items-center">
                    <ChromeHandle
                        id={VIDEO_SCRUBBER}
                        box={scrubberStored}
                        className={layout.scrubberClass}
                    >
                        <div className="absolute inset-x-0 rounded-full" style={{ height: thickness, backgroundColor: scrubberBox.trackColor }} />
                        <div
                            className="absolute left-0 rounded-full"
                            style={{
                                height: thickness,
                                width: `${progress}%`,
                                backgroundColor: scrubberBox.fillColor,
                            }}
                        />
                        <input
                            type="range"
                            min={0}
                            max={duration || 0}
                            step={0.05}
                            value={currentTime}
                            onChange={(event) => onSeek(event.target.value)}
                            className="absolute inset-0 w-full cursor-pointer opacity-0"
                            aria-label="Seek video"
                        />
                    </ChromeHandle>
                </div>

                <div className="relative flex items-center justify-end">
                    {layout.timeStandIn ? (
                        <span aria-hidden="true" className="invisible inline-flex items-center whitespace-nowrap" style={timeStyle}>
                            {timeLabel}
                        </span>
                    ) : null}
                    <ChromeHandle
                        id={VIDEO_TIME}
                        box={timeStored}
                        className={layout.timeClass}
                        style={timeStyle}
                    >
                        {timeLabel}
                    </ChromeHandle>
                </div>
            </div>

            {description ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center px-4 pb-5">
                    <ChromeHandle
                        id={mediaTitle.id}
                        box={mediaTitle.box}
                        className="pointer-events-auto inline-block text-center"
                        style={mediaTitle.active
                            ? mediaTitle.style
                            : videoTextStyle(captionBox, theme)}
                    >
                        {description}
                    </ChromeHandle>
                </div>
            ) : null}
        </>
    );
}

VideoControls.propTypes = {
    isPlaying: PropTypes.bool,
    currentTime: PropTypes.number,
    duration: PropTypes.number,
    description: PropTypes.string,
    onToggle: PropTypes.func.isRequired,
    onSeek: PropTypes.func.isRequired,
};
